/**
 * Importações: por onde a OS entra.
 *
 * Em produção quem traz o PDF é o agente instalado no computador do sistema da
 * gráfica. Esta tela existe para três coisas que o agente não resolve: a
 * contingência (arrastar PDFs à mão), o log (o que entrou, quando e como) e a
 * **quarentena** — os arquivos que o leitor não entendeu, com o PDF à mão e o
 * motivo escrito em português.
 *
 * Reprocessar roda o leitor atual sobre o PDF guardado. É o caminho quando o
 * layout da OS muda: leitor novo, e o que estava parado entra sem redigitar.
 */

import { formato, sha256Bytes, urlTemporaria } from '@pcpgbu/dominio';
import type { Importacao } from '@pcpgbu/dominio';
import { perfilDeImportacao } from '@pcpgbu/leitor-os';
import { aviso, botao, cabecalhoDePagina, h, notificar, render, sf, tituloDeSecao, vazio, type Filho } from '@pcpgbu/ui';
import type { Aplicacao } from '../estado.js';
import { avisarEmFolha } from '../dialogos.js';
import { prepararLeitor } from '../leitor.js';

interface Progresso {
  total: number;
  feitos: number;
  aplicadas: number;
  duplicadas: number;
  quarentena: number;
  arquivo: string;
}

let emAndamento: Progresso | null = null;

export async function telaImportacoes(app: Aplicacao, destino: HTMLElement): Promise<void> {
  const importacoes = await app.repo.importacoes();
  const quarentena = importacoes.filter((i) => i.status === 'quarentena');

  render(destino, [
    h('.vstack.gap-4', [
      cabecalhoDePagina({
        olho: 'Entrada de OS',
        titulo: 'Importações',
        lead: 'Por onde a OS entra. O agente traz sozinho da pasta do sistema da gráfica; aqui dá para arrastar PDF à mão e ver o que ficou de fora.',
      }),

      zonaDeSoltar(app),
      emAndamento ? cartaoProgresso(emAndamento) : null,
      quarentena.length > 0 ? blocoQuarentena(app, quarentena) : null,
      blocoHistorico(app, importacoes),
    ]),
  ]);
}

/* ------------------------------------------------------------------ */

function zonaDeSoltar(app: Aplicacao): HTMLElement {
  const entrada = h('input', {
    type: 'file',
    accept: 'application/pdf,.pdf',
    multiple: true,
    style: { display: 'none' },
    onchange: (ev: Event) => {
      const arquivos = [...((ev.target as HTMLInputElement).files ?? [])];
      if (arquivos.length) void importarArquivos(app, arquivos);
      (ev.target as HTMLInputElement).value = '';
    },
  });

  const zona = h(
    '.pcp-solta',
    {
      ondragover: (ev: DragEvent) => {
        ev.preventDefault();
        zona.classList.add('ativa');
      },
      ondragleave: () => zona.classList.remove('ativa'),
      ondrop: (ev: DragEvent) => {
        ev.preventDefault();
        zona.classList.remove('ativa');
        const arquivos = [...(ev.dataTransfer?.files ?? [])].filter(
          (f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf'),
        );
        if (arquivos.length) void importarArquivos(app, arquivos);
      },
      onclick: () => entrada.click(),
      style: { cursor: 'pointer' },
    },
    [
      sf('tray'),
      h('.t-title3', { style: { marginTop: '8px' } }, 'Arraste os PDFs das OS aqui'),
      h('.t-callout.t-secondary', 'Pode soltar vários de uma vez. Nada é enviado para fora: a leitura acontece neste computador.'),
      entrada,
    ],
  );

  return h('.cartao', [
    h('.hstack', [
      h('.bloco-t', 'Importar OS'),
      h('.spacer'),
      botao('Carregar as 21 de exemplo', () => carregarExemplos(app), {
        icone: 'doc-on-doc',
        pequeno: true,
      }),
    ]),
    zona,
  ]);
}

function cartaoProgresso(p: Progresso): HTMLElement {
  const pct = p.total > 0 ? Math.round((p.feitos / p.total) * 100) : 0;
  return h('.cartao', [
    h('.hstack.gap-3', [
      h('.mac-spinner'),
      h('.vstack', { style: { gap: '2px', flex: '1' } }, [
        h('.t-body-emph', `Lendo ${p.feitos + 1} de ${p.total}…`),
        h('.t-subhead.t-secondary.t-truncate', p.arquivo),
      ]),
      h('span.t-title3.pcp-num', `${pct}%`),
    ]),
    h('.mac-progress', { style: { marginTop: '10px', '--val': `${pct}%` } }),
  ]);
}

/* ------------------------------------------------------------------ */

async function importarArquivos(app: Aplicacao, arquivos: File[]): Promise<void> {
  emAndamento = {
    total: arquivos.length,
    feitos: 0,
    aplicadas: 0,
    duplicadas: 0,
    quarentena: 0,
    arquivo: arquivos[0]?.name ?? '',
  };
  app.redesenhar();

  const problemas: string[] = [];

  // O leitor de PDF é carregado só aqui: é a única tela que lê arquivo, e o
  // resto do aplicativo não pode depender dele para abrir.
  const leitorPronto = await prepararLeitor();
  if (!leitorPronto) {
    emAndamento = null;
    app.redesenhar();
    await avisarEmFolha(
      'O leitor de PDF não carregou',
      'Sem ele não dá para ler as OS. Recarregue a página; se continuar, é a instalação do aplicativo que precisa ser conferida. O resto do sistema segue funcionando.',
    );
    return;
  }

  for (const arquivo of arquivos) {
    emAndamento.arquivo = arquivo.name;
    app.redesenhar();

    try {
      const dados = new Uint8Array(await arquivo.arrayBuffer());
      const leitura = await perfilDeImportacao().ler(dados);
      const hashPdf = await sha256Bytes(dados);

      const r = await app.repo.aplicarOsLida({
        osLida: leitura.os,
        motivoQuarentena: leitura.motivoQuarentena,
        avisos: leitura.avisos,
        arquivo: arquivo.name,
        hashPdf,
        origem: 'upload',
        pdf: dados,
        imagem: leitura.imagem,
        por: app.sessao.pessoaId,
        versaoLeitor: perfilDeImportacao().versao,
      });

      if (r.importacao.status === 'aplicada') emAndamento.aplicadas += 1;
      else if (r.importacao.status === 'duplicada') emAndamento.duplicadas += 1;
      else {
        emAndamento.quarentena += 1;
        problemas.push(`${arquivo.name}: ${r.motivoQuarentena ?? 'motivo desconhecido'}`);
      }
    } catch (erro) {
      emAndamento.quarentena += 1;
      problemas.push(`${arquivo.name}: ${erro instanceof Error ? erro.message : String(erro)}`);
    }

    emAndamento.feitos += 1;
    app.redesenhar();
  }

  const resumo = emAndamento;
  emAndamento = null;
  app.redesenhar();

  notificar({
    titulo:
      resumo.aplicadas > 0
        ? `${resumo.aplicadas} ${resumo.aplicadas === 1 ? 'OS entrou' : 'OS entraram'}`
        : 'Nenhuma OS nova',
    corpo: [
      resumo.duplicadas ? `${resumo.duplicadas} já estavam no sistema` : null,
      resumo.quarentena ? `${resumo.quarentena} em quarentena` : null,
    ]
      .filter(Boolean)
      .join(' · '),
    icone: resumo.quarentena > 0 ? 'exclamationmark-triangle' : 'checkmark-circle',
  });

  if (problemas.length > 0) {
    await avisarEmFolha(
      'Alguns arquivos não entraram',
      h(
        '.vstack.gap-2',
        [
          h('p.t-callout.t-secondary', { style: { margin: 0 } },
            'Eles ficaram na quarentena, com o PDF guardado. Nada foi importado pela metade.'),
          ...problemas.map((p) => h('.t-callout', p)),
        ],
      ),
    );
  }
}

/**
 * Carrega as 21 OS de amostra que são publicadas junto com o aplicativo.
 *
 * Serve a três usos reais: mostrar o sistema ao cliente sem pedir que ele
 * arraste arquivos, conferir uma versão recém-publicada, e abrir o aplicativo
 * já com dado (`?exemplo=1`) para uma captura de tela ou uma demonstração.
 */
export async function carregarExemplos(app: Aplicacao, silencioso = false): Promise<void> {
  const nomes = Array.from({ length: 21 }, (_, i) => `OS-${113900 + i}.pdf`);
  emAndamento = { total: nomes.length, feitos: 0, aplicadas: 0, duplicadas: 0, quarentena: 0, arquivo: '' };
  app.redesenhar();

  const arquivos: File[] = [];
  for (const nome of nomes) {
    try {
      const resposta = await fetch(`./amostras/${nome}`);
      if (!resposta.ok) continue;
      arquivos.push(new File([await resposta.blob()], nome, { type: 'application/pdf' }));
    } catch {
      /* amostra ausente: segue com as que existirem */
    }
  }

  emAndamento = null;
  if (arquivos.length === 0) {
    if (!silencioso) {
      await avisarEmFolha(
        'As OS de exemplo não estão nesta cópia',
        'Elas são publicadas junto com o aplicativo em `publico/amostras/`. Arraste os PDFs da gráfica na área acima.',
      );
    }
    app.redesenhar();
    return;
  }
  await importarArquivos(app, arquivos);
}

/* ------------------------------------------------------------------ */

function blocoQuarentena(app: Aplicacao, itens: Importacao[]): HTMLElement {
  return h('.cartao', { style: { boxShadow: 'var(--shadow-1), 0 0 0 1px color-mix(in srgb, var(--sys-orange) 40%, transparent)' } }, [
    tituloDeSecao(`Quarentena · ${itens.length}`),
    aviso(
      'aviso',
      'Estes arquivos não foram lidos e nada deles entrou. O PDF está guardado: dá para abrir, entender e reprocessar depois de corrigir o leitor.',
    ),
    h(
      '.vstack.gap-2',
      { style: { marginTop: 'var(--sp-3)' } },
      itens.map((i) =>
        h('.hstack.gap-3', { style: { padding: '8px 0', borderTop: '1px solid var(--separator)' } }, [
          h('.vstack', { style: { gap: '1px', flex: '1', minWidth: '0' } }, [
            h('.t-body-emph.t-truncate', i.arquivo),
            h('.t-callout', { style: { color: 'var(--sys-orange)' } }, i.erro ?? '—'),
            h('.t-subhead.t-tertiary', `${formato.dataHoraCurta(i.recebidoEm)} · leitor ${i.versaoLeitor} · ${i.origem === 'agente' ? 'agente' : 'arrastado à mão'}`),
          ]),
          i.pdfRef
            ? botao('Ver o PDF', async () => {
                const arq = await app.repo.arquivo(i.pdfRef as string);
                if (!arq) return;
                const url = urlTemporaria(arq.dados, 'application/pdf');
                window.open(url, '_blank', 'noopener');
              }, { icone: 'doc-text', pequeno: true })
            : null,
          i.pdfRef
            ? botao('Reprocessar', () => reprocessar(app, i), { icone: 'arrow-clockwise', pequeno: true })
            : null,
        ]),
      ),
    ),
  ]);
}

async function reprocessar(app: Aplicacao, i: Importacao): Promise<void> {
  if (!i.pdfRef) return;
  const arq = await app.repo.arquivo(i.pdfRef);
  if (!arq) return;

  const leitura = await perfilDeImportacao().ler(arq.dados);
  const r = await app.repo.aplicarOsLida({
    osLida: leitura.os,
    motivoQuarentena: leitura.motivoQuarentena,
    avisos: leitura.avisos,
    arquivo: i.arquivo,
    // Hash novo: é uma tentativa nova, e o histórico da anterior fica.
    hashPdf: `${i.hashPdf}-reproc-${Date.now()}`,
    origem: i.origem,
    pdf: arq.dados,
    imagem: leitura.imagem,
    por: app.sessao.pessoaId,
    versaoLeitor: perfilDeImportacao().versao,
  });

  notificar({
    titulo:
      r.importacao.status === 'aplicada'
        ? `OS ${r.importacao.osNumero} entrou`
        : 'Continua sem ser lida',
    corpo: r.motivoQuarentena ?? undefined,
    icone: r.importacao.status === 'aplicada' ? 'checkmark-circle' : 'exclamationmark-triangle',
  });
  app.redesenhar();
}

/* ------------------------------------------------------------------ */

function blocoHistorico(app: Aplicacao, itens: Importacao[]): HTMLElement {
  if (itens.length === 0) {
    return h('.cartao', vazio(
      'clock-arrow-circlepath',
      'Nenhuma importação ainda',
      'Cada arquivo lido fica registrado aqui: de onde veio, quando, e o que virou.',
    ));
  }

  const rotulo: Record<string, { texto: string; cor: string }> = {
    aplicada: { texto: 'aplicada', cor: 'var(--sys-green)' },
    duplicada: { texto: 'já existia', cor: 'var(--label-2)' },
    quarentena: { texto: 'quarentena', cor: 'var(--sys-orange)' },
    reprocessada: { texto: 'reprocessada', cor: 'var(--sys-blue)' },
  };

  return h('.cartao', { style: { padding: '0', overflow: 'hidden' } }, [
    h('.hstack', { style: { padding: 'var(--sp-3) var(--sp-4) var(--sp-2)' } }, [
      h('.bloco-t', { style: { padding: 0 } }, `Histórico · ${itens.length}`),
    ]),
    h('table.tab', [
      h('thead', h('tr', [
        h('th', 'Arquivo'),
        h('th', { style: { width: '92px' } }, 'OS'),
        h('th', { style: { width: '110px' } }, 'Resultado'),
        h('th', { style: { width: '92px' } }, 'Origem'),
        h('th', { style: { width: '128px' } }, 'Quando'),
      ])),
      h('tbody',
        itens.slice(0, 200).map((i) => {
          const r = rotulo[i.status] ?? { texto: i.status, cor: 'var(--label-2)' };
          return h(
            `tr${i.osNumero ? '.clicavel' : ''}`,
            { onclick: i.osNumero ? () => app.roteador.ir(`/os/${i.osNumero}`) : undefined },
            [
              h('td.t-truncate', [
                i.arquivo,
                i.avisos.length > 0
                  ? h('span.t-subhead', { style: { color: 'var(--sys-orange)' }, title: i.avisos.join('\n') },
                      ` · ${i.avisos.length} aviso${i.avisos.length > 1 ? 's' : ''}`)
                  : null,
              ]),
              h('td.pcp-mono', i.osNumero ? String(i.osNumero) : '—'),
              h('td', h('span', { style: { color: r.cor, fontWeight: '600' } }, r.texto)),
              h('td.t-subhead.t-secondary', i.origem === 'agente' ? 'agente' : 'à mão'),
              h('td.t-subhead.t-secondary', formato.dataHoraCurta(i.recebidoEm)),
            ],
          );
        }),
      ),
    ]),
  ]);
}

export type { Filho };
